import React, { useState } from "react";
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { format } from "date-fns";
import { useGetUserRoleAuditLogs } from "../../../api/queries";
import { UserRoleAuditLog } from "../../../types/rbac";
import Card from "../../../componentLibrary/Card";
import Input from "../../../componentLibrary/Input";
import Button from "../../../componentLibrary/Button";
import Spinner from "../../../componentLibrary/Spinner";

const ENTRIES_PER_PAGE = 50;

const AuditLogView: React.FC = () => {
  const [searchText, setSearchText] = useState("");
  const [filterSearch, setFilterSearch] = useState<string | undefined>(
    undefined,
  );
  const [currentPage, setCurrentPage] = useState(0);

  const skip = currentPage * ENTRIES_PER_PAGE;

  const { data, isLoading, error, refetch } = useGetUserRoleAuditLogs({
    search: filterSearch,
    limit: ENTRIES_PER_PAGE,
    skip,
  });

  const handleSearch = () => {
    setFilterSearch(searchText.trim() || undefined);
    setCurrentPage(0);
  };

  const handleNextPage = () => {
    if (data && skip + ENTRIES_PER_PAGE < data.total) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy 'at' hh:mm a");
    } catch {
      return dateString;
    }
  };

  const getActionBadgeColor = (actionType: string) => {
    switch (actionType) {
      case "ROLE_ASSIGNED":
        return "bg-green-100 text-green-800";
      case "ROLE_REMOVED":
        return "bg-red-100 text-red-800";
      case "MARKED_FOR_DELETION":
        return "bg-orange-100 text-orange-800";
      case "DELETION_CANCELLED":
        return "bg-blue-100 text-blue-800";
      case "ACCOUNT_DELETED":
        return "bg-gray-800 text-white";
      case "CSV_EXPORT":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getActionLabel = (actionType: string) => {
    switch (actionType) {
      case "ROLE_ASSIGNED":
        return "Role Assigned";
      case "ROLE_REMOVED":
        return "Role Removed";
      case "MARKED_FOR_DELETION":
        return "Marked for Deletion";
      case "DELETION_CANCELLED":
        return "Deletion Cancelled";
      case "ACCOUNT_DELETED":
        return "Account Deleted";
      case "CSV_EXPORT":
        return "CSV Export";
      default:
        return actionType;
    }
  };

  const formatRoleChange = (log: UserRoleAuditLog) => {
    if (!log.rolesChanged) {
      return "—";
    }
    const { beforeNames, afterNames } = log.rolesChanged;

    if (beforeNames.length === 0 && afterNames.length === 1) {
      return "None → " + afterNames.join(", ");
    }

    if (beforeNames.length === 1 && afterNames.length === 0) {
      return beforeNames.join(", ") + " → None";
    }

    return beforeNames.join(", ") + " → " + afterNames.join(", ");
  };

  return (
    <div className="space-y-6">
      <Card>
        <h2 className="text-xl sm:text-2xl font-bold text-electrum mb-4">
          Audit Log
        </h2>

        <div className="flex flex-col items-center sm:flex-row gap-2 sm:gap-4 mb-6">
          <div className="flex-1">
            <Input
              placeholder="Search by email, username, or user sub"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
            />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleSearch}
              className="flex items-center justify-center gap-2"
              size="md"
            >
              <MagnifyingGlassIcon className="w-5 h-5" />
              <span className="hidden sm:inline">Search</span>
            </Button>
            <Button
              onClick={() => refetch()}
              variant="secondary"
              className="flex items-center justify-center gap-2"
              size="md"
            >
              <ArrowPathIcon className="w-5 h-5" />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
          </div>
        </div>

        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <Spinner className="w-12 h-12" />
          </div>
        )}

        {error && (
          <div className="text-red-600 p-4 bg-red-50 rounded-md">
            Error loading audit logs: {error.message}
          </div>
        )}

        {!isLoading && !error && data && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100 border-b-2 border-gray-300">
                    <th className="text-left p-3 font-semibold">Timestamp</th>
                    <th className="text-left p-3 font-semibold">Changed By</th>
                    <th className="text-left p-3 font-semibold">Action</th>
                    <th className="text-left p-3 font-semibold">Target User</th>
                    <th className="text-left p-3 font-semibold">
                      Before → After
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.logs.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center p-8 text-gray-500">
                        No audit logs found
                        {filterSearch && " matching your search"}
                      </td>
                    </tr>
                  ) : (
                    data.logs.map((log) => (
                      <tr
                        key={log._id}
                        className="border-b border-gray-200 hover:bg-gray-50"
                      >
                        <td className="p-3 text-sm">
                          {formatDate(log.timestamp)}
                        </td>
                        <td className="p-3">
                          <div className="text-sm">
                            <div className="font-medium">
                              {log.changedBy.userName}
                            </div>
                            <div className="text-gray-500 text-xs">
                              {log.changedBy.email}
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${getActionBadgeColor(log.actionType)}`}
                          >
                            {getActionLabel(log.actionType)}
                          </span>
                        </td>
                        <td className="p-3">
                          {log.actionType === "CSV_EXPORT" ? (
                            <span className="text-gray-400 text-sm">—</span>
                          ) : (
                            <div className="text-sm">
                              <div className="font-medium">
                                {log.targetUser.userName}
                              </div>
                              <div className="text-gray-500 text-xs">
                                {log.targetUser.email}
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="p-3 text-sm">
                          {log.actionType === "CSV_EXPORT" ? (
                            <div className="space-y-0.5">
                              {log.metadata?.exportedCount !== undefined && (
                                <div>
                                  <span className="text-gray-500">Rows: </span>
                                  {log.metadata.exportedCount}
                                </div>
                              )}
                              {log.metadata?.fields &&
                                log.metadata.fields.length > 0 && (
                                  <div>
                                    <span className="text-gray-500">
                                      Fields:{" "}
                                    </span>
                                    {log.metadata.fields.join(", ")}
                                  </div>
                                )}
                              {log.metadata?.filters &&
                                Object.keys(log.metadata.filters).length >
                                  0 && (
                                  <div>
                                    <span className="text-gray-500">
                                      Filters:{" "}
                                    </span>
                                    {Object.entries(log.metadata.filters)
                                      .map(([k, v]) => `${k}: ${v}`)
                                      .join(", ")}
                                  </div>
                                )}
                            </div>
                          ) : (
                            formatRoleChange(log)
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between items-center mt-6">
              <div className="text-sm text-gray-600">
                Showing {skip + 1} to{" "}
                {Math.min(skip + ENTRIES_PER_PAGE, data.total)} of {data.total}{" "}
                entries
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handlePreviousPage}
                  disabled={currentPage === 0}
                  variant="secondary"
                  size="sm"
                >
                  Previous
                </Button>
                <Button
                  onClick={handleNextPage}
                  disabled={skip + ENTRIES_PER_PAGE >= data.total}
                  variant="secondary"
                  size="sm"
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default AuditLogView;
